// assets/component-cart-recommendations.js
// "Don't forget to add" cart recommendations. Picks 3 products at random from
// the server-rendered pool, excludes any product already in the cart, and mounts
// them into a native side-scrolling track (free scrolling, no snapping) with
// pagination dots that stay in sync with the scroll position. Touch and trackpads scroll the
// track natively; mouse users get drag-to-scroll (pointer events) plus the dots.
// Re-reconciles on every cart change so a product disappears from the
// suggestions the moment it lands in the cart.
//
// The product cards themselves are server-rendered (in an inert <template>) and
// only cloned here — the random selection + in-cart filtering must run on the
// client (random can't survive full-page caching). The track / dots scaffold is
// the minimal structural markup needed around the cloned cards.
//
// The dots hide themselves whenever the track has nothing to scroll (e.g. the
// cart page grid, where all cards fit side by side), so the same module serves
// both the drawer and the cart page.
(() => {
  const app = window.Digitaliz;
  if (!app) return;
  window.DigitalizModules = window.DigitalizModules || {};
  if (window.DigitalizModules.CartRecommendations) return;

  const SHOW_COUNT = 3;
  const MIN_QTY = (window.themeSettings && Number(window.themeSettings.minQty)) || 2;

  class CartRecommendations {
    constructor(el) {
      this.el = el;
      this.pool = el.querySelector("[data-cdr-pool]");
      this.mount = el.querySelector("[data-cdr-mount]");
      this.track = null;
      this.dots = null;
      this.resizeObserver = null;
      this.scrollFrame = 0;
      this.shownIds = [];
      this.cardsById = new Map();
      this.isAdding = false;
      this.drag = { active: false, moved: false, startX: 0, startScroll: 0 };
      this.onTrackScroll = this.onTrackScroll.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onTrackClick = this.onTrackClick.bind(this);
      this.onDragStart = (event) => event.preventDefault();
      this.init();
    }

    init() {
      if (!this.pool || !this.mount) return;
      this.pool.content.querySelectorAll("[data-cdr-card]").forEach((card) => {
        const id = card.dataset.cdrProductId;
        if (id && !this.cardsById.has(id)) this.cardsById.set(id, card);
      });
      this.bind();
      // First paint with no cart knowledge; the init cart refresh corrects it.
      this.reconcile(null);
    }

    bind() {
      this.el.addEventListener("click", (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const dot = target.closest("[data-cdr-dot]");
        if (dot) {
          this.goTo(Number(dot.dataset.cdrDot));
          return;
        }

        const btn = target.closest("[data-cdr-add]");
        if (!btn) return;
        const card = btn.closest("[data-cdr-card]");
        if (card) this.add(card, btn);
      });

      app.on(app.events.CART_REFRESH, (event) => {
        this.reconcile(event.detail && event.detail.cart);
      });
    }

    inCartIds(cart) {
      const set = new Set();
      if (cart && Array.isArray(cart.items)) {
        cart.items.forEach((item) => set.add(String(item.product_id)));
      }
      return set;
    }

    reconcile(cart) {
      // Don't show recommendations when the cart is empty.
      if (cart && cart.item_count === 0) {
        this.shownIds = [];
        this.teardown();
        this.el.hidden = true;
        return;
      }

      const inCart = this.inCartIds(cart);

      // Keep still-valid shown ids (preserve order), then backfill at random.
      let next = this.shownIds.filter((id) => !inCart.has(id));
      if (next.length < SHOW_COUNT) {
        const used = new Set(next);
        const candidates = [...this.cardsById.keys()].filter(
          (id) => !inCart.has(id) && !used.has(id)
        );
        this.shuffle(candidates);
        while (next.length < SHOW_COUNT && candidates.length) {
          next.push(candidates.shift());
        }
      }
      next = next.slice(0, SHOW_COUNT);

      if (this.track && this.sameList(next, this.shownIds)) return;
      this.shownIds = next;
      this.render(next);
    }

    // Removes the mounted track/dots and their listeners.
    teardown() {
      if (this.track) {
        this.track.removeEventListener("scroll", this.onTrackScroll);
        this.unbindDrag(this.track);
      }
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
      if (this.scrollFrame) {
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = 0;
      }
      this.track = null;
      this.dots = null;
      this.mount.replaceChildren();
    }

    render(ids) {
      this.teardown();

      if (!ids.length) {
        this.el.hidden = true;
        return;
      }

      const track = document.createElement("ul");
      track.className = "cart-rec__track";
      track.setAttribute("data-cdr-track", "");

      ids.forEach((id) => {
        const source = this.cardsById.get(id);
        if (!source) return;
        const slide = document.createElement("li");
        slide.className = "cart-rec__slide";
        slide.appendChild(source.cloneNode(true));
        track.appendChild(slide);
      });

      const dots = document.createElement("div");
      dots.className = "cart-rec__dots";
      dots.setAttribute("data-cdr-dots", "");
      const count = track.children.length;
      for (let i = 0; i < count; i++) {
        const dot = document.createElement("button");
        dot.type = "button";
        dot.className = "cart-rec__dot";
        dot.dataset.cdrDot = String(i);
        // Language-neutral position label ("1 / 3").
        dot.setAttribute("aria-label", `${i + 1} / ${count}`);
        dots.appendChild(dot);
      }

      this.mount.appendChild(track);
      this.mount.appendChild(dots);
      this.track = track;
      this.dots = dots;
      this.el.hidden = false;

      track.addEventListener("scroll", this.onTrackScroll, { passive: true });
      this.bindDrag(track);
      if (typeof ResizeObserver === "function") {
        this.resizeObserver = new ResizeObserver(() => this.syncDots());
        this.resizeObserver.observe(track);
      }
      this.syncDots();
    }

    onTrackScroll() {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = 0;
        this.syncDots();
      });
    }

    // Index of the slide whose leading edge sits closest to the track's leading
    // edge. Rect-based so it works in both LTR and RTL without sign juggling.
    activeIndex() {
      if (!this.track) return 0;
      const slides = [...this.track.children];
      if (!slides.length) return 0;
      const trackRect = this.track.getBoundingClientRect();
      const rtl = getComputedStyle(this.track).direction === "rtl";
      let best = 0;
      let bestDistance = Infinity;
      slides.forEach((slide, i) => {
        const rect = slide.getBoundingClientRect();
        const distance = rtl
          ? Math.abs(trackRect.right - rect.right)
          : Math.abs(rect.left - trackRect.left);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = i;
        }
      });
      return best;
    }

    syncDots() {
      if (!this.track || !this.dots) return;
      // No overflow → nothing to page through (cart page grid, wide viewports).
      const scrollable = this.track.scrollWidth > this.track.clientWidth + 1;
      this.dots.hidden = !scrollable;
      this.track.classList.toggle("is-scrollable", scrollable);
      if (!scrollable) return;

      const active = this.activeIndex();
      [...this.dots.children].forEach((dot, i) => {
        const isActive = i === active;
        dot.classList.toggle("is-active", isActive);
        dot.setAttribute("aria-current", isActive ? "true" : "false");
      });
    }

    goTo(index) {
      if (!this.track) return;
      const slide = this.track.children[index];
      if (!slide) return;
      const trackRect = this.track.getBoundingClientRect();
      const rect = slide.getBoundingClientRect();
      const rtl = getComputedStyle(this.track).direction === "rtl";
      const delta = rtl ? rect.right - trackRect.right : rect.left - trackRect.left;
      this.track.scrollBy({ left: delta, behavior: "smooth" });
    }

    // ── Mouse drag-to-scroll ──────────────────────────────────────────────
    // Touch and pen pointers are left to native scrolling; only the mouse needs
    // help (no horizontal wheel, hidden scrollbar). The track follows the pointer
    // and stays wherever it is released. A drag never triggers the card's click.
    //
    // The pointer is captured only once the pointer has actually moved past the
    // drag threshold — never on pointerdown. Capturing on pointerdown retargets
    // the following click to the track itself (pointer-capture click semantics),
    // so a plain mouse click on "+ ADD" or the product link never reached them.

    bindDrag(track) {
      track.addEventListener("pointerdown", this.onPointerDown);
      track.addEventListener("pointermove", this.onPointerMove);
      track.addEventListener("pointerup", this.onPointerUp);
      track.addEventListener("pointercancel", this.onPointerUp);
      track.addEventListener("dragstart", this.onDragStart);
      track.addEventListener("click", this.onTrackClick, true);
    }

    unbindDrag(track) {
      track.removeEventListener("pointerdown", this.onPointerDown);
      track.removeEventListener("pointermove", this.onPointerMove);
      track.removeEventListener("pointerup", this.onPointerUp);
      track.removeEventListener("pointercancel", this.onPointerUp);
      track.removeEventListener("dragstart", this.onDragStart);
      track.removeEventListener("click", this.onTrackClick, true);
      track.classList.remove("is-dragging");
      this.drag.active = false;
      this.drag.moved = false;
    }

    onPointerDown(event) {
      this.drag.moved = false;
      if (!this.track || event.pointerType !== "mouse" || event.button !== 0) return;
      if (!this.track.classList.contains("is-scrollable")) return;
      this.drag.active = true;
      this.drag.startX = event.clientX;
      this.drag.startScroll = this.track.scrollLeft;
    }

    onPointerMove(event) {
      if (!this.drag.active || !this.track) return;
      const dx = event.clientX - this.drag.startX;
      if (!this.drag.moved) {
        if (Math.abs(dx) < 4) return;
        this.drag.moved = true;
        this.track.classList.add("is-dragging");
        if (typeof this.track.setPointerCapture === "function") {
          this.track.setPointerCapture(event.pointerId);
        }
      }
      this.track.scrollLeft = this.drag.startScroll - dx;
    }

    onPointerUp(event) {
      if (!this.drag.active || !this.track) return;
      this.drag.active = false;
      if (!this.drag.moved) return;
      if (typeof this.track.releasePointerCapture === "function") {
        try {
          this.track.releasePointerCapture(event.pointerId);
        } catch (error) {
          // Capture already released.
        }
      }
      this.track.classList.remove("is-dragging");
    }

    // Capture-phase: swallow the click that follows a drag so a drag that ends
    // on "+ Add" or a product link doesn't activate it.
    onTrackClick(event) {
      if (!this.drag.moved) return;
      this.drag.moved = false;
      event.preventDefault();
      event.stopPropagation();
    }

    async add(card, btn) {
      if (this.isAdding) return;
      const variantId = Number(card.dataset.cdrVariantId);
      if (!variantId) return;

      this.isAdding = true;
      btn.disabled = true;
      try {
        if (!app.cart || typeof app.cart.add !== "function") {
          throw new Error("Cart unavailable");
        }
        // Added at the theme minimum quantity (non-exempt), like other adds.
        await app.cart.add([{ id: variantId, quantity: MIN_QTY }]);
        // The cart refresh that follows reconciles this product out of the list.
      } catch (error) {
        // Errors surface via the CART_ERROR toast.
      } finally {
        this.isAdding = false;
        btn.disabled = false;
      }
    }

    shuffle(arr) {
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
      return arr;
    }

    sameList(a, b) {
      return a.length === b.length && a.every((value, i) => value === b[i]);
    }
  }

  window.DigitalizModules.CartRecommendations = CartRecommendations;

  function initRec(root) {
    (root || document).querySelectorAll("[data-cart-rec]").forEach((el) => {
      if (el.dataset.cdrBound === "true") return;
      el.dataset.cdrBound = "true";
      new CartRecommendations(el);
    });
  }

  if (document.readyState !== "loading") {
    initRec(document);
  } else {
    document.addEventListener("DOMContentLoaded", () => initRec(document));
  }
})();
